import React, { useCallback, useMemo, useContext, useState } from 'react';
import { IStyle } from 'Styles';
import './style.scss';
import { handleTableFilter } from './baseGroup';
import { GroupDTO, IGroupDTO, UserDTO } from 'business/models/Models';
import { UserGroupContext } from '../../Licensing/userSetup';
import { showAlertSnackBar } from 'utils/Global';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import HeaderBar from 'components/main/Setup/CrystalContainer/HeaderBar';
import RMTable from 'components/customComponents/Controls/RMTable';
import { RMRadioButton } from 'components/customComponents/Layers/InputGroups';
import { ALL_ROCKMAKER_USERS } from '../constants';

interface Props {
  selectedUser: UserDTO;
}

const getTableColumns: object[] = [
  {
    accessor: 'groupName',
    Filter: handleTableFilter('Group Name'),
  },
  {
    accessor: 'primaryGroup',
    Header: 'Primary Group',
    className: '!justify-center',
  },
];

const groupsNotAllowedToBeRemoved = [ALL_ROCKMAKER_USERS];
const adminGroupName = 'Administrators';
const adminUserName = 'Administrator';

const GroupList = (props: Props) => {
  const { selectedUser } = props;
  const { tempState, setTempState } = useContext(UserGroupContext);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const selectedGroupIds = useMemo(
    () => selectedGroups.map((g) => g.id),
    [selectedGroups],
  );

  const updateSelectedUserGroups = useCallback(
    (groups: IGroupDTO[]) => {
      setTempState((prv) => {
        let selectedUserIndex = null;
        const filteredUsers = prv.users.filter((u, _ix) => {
          if (u.id !== selectedUser.id) {
            return true;
          }

          selectedUserIndex = _ix;
          return false;
        });

        filteredUsers.splice(selectedUserIndex, 0, {
          ...selectedUser,
          groups,
        });

        return {
          ...prv,
          users: filteredUsers,
        };
      });
    },
    [selectedUser],
  );

  if (selectedRows.length && selectedGroups.length && !selectedUser) {
    setSelectedGroups([]);
    setSelectedRows([]);
  }

  /**
   * Remove Group from users' assigned list.
   */
  const handleDeleteGroup = useCallback(() => {
    const groups: IGroupDTO[] = selectedUser.groups.filter(
      (group) => !selectedGroupIds.includes(group.id),
    );

    const shouldNotRemoveAdminFromAdminGroup =
      selectedUser.name === adminUserName &&
      selectedGroups.some((g) => g.name === adminGroupName);
    const groupsNotAllowedMessage = `${groupsNotAllowedToBeRemoved.toString()} ${
      shouldNotRemoveAdminFromAdminGroup ? adminGroupName : ''
    }`;

    if (
      shouldNotRemoveAdminFromAdminGroup ||
      selectedGroups.some((g) => groupsNotAllowedToBeRemoved.includes(g.name))
    ) {
      showAlertSnackBar(
        EAlertType.ERROR,
        ESnackBarPosition.TOP,
        `Not Allowed to remove ${groupsNotAllowedMessage} group(s)`,
      );
      return;
    }

    setTempState((prv) => {
      const filteredGroups = prv.groups.map((_group: GroupDTO, _ix) => {
        if (!selectedGroupIds.includes(_group.id)) {
          return _group;
        }

        _group.users = _group.users.filter(
          (u) => !(selectedGroupIds.includes(_group.id) && selectedUser.id === u.id),
        );

        return _group;
      });

      return {
        ...prv,
        groups: filteredGroups,
      };
    });

    updateSelectedUserGroups(groups);
    setSelectedGroups([]);
  }, [selectedGroupIds, selectedUser, selectedGroups]);

  const handlePrimaryGroupSelection = useCallback(
    (groupId: number) => {
      const groups: IGroupDTO[] = selectedUser.groups.map((group) => ({
        ...group,
        isPrimaryGroup: group.id === groupId,
      }));
      updateSelectedUserGroups(groups);
    },
    [selectedUser],
  );

  const groupData = useMemo(
    () =>
      selectedUser?.groups?.map((group) => ({
        id: group.id,
        groupName: group.name,
        primaryGroup: (
          <RMRadioButton
            selected={group.isPrimaryGroup ? group.id : ''}
            onClick={() => handlePrimaryGroupSelection(group.id)}
            value={group.id}
            label={''} // Optional, can be used for display purposes
          />
        ),
      })) || [],
    [selectedUser, handlePrimaryGroupSelection, tempState, selectedRows],
  );
  const handleSelectionChange = useCallback(
    (groupIndices: any) => {
      setSelectedRows(groupIndices);
      setSelectedGroups(
        selectedUser.groups.filter((_v, _ix) => groupIndices.includes(_ix)),
      );
    },
    [selectedUser, setSelectedGroups],
  );

  return (
    <>
      <HeaderBar
        title={'Group List'}
        onDelete={handleDeleteGroup}
        enableEditDelete={selectedGroups.length !== 0}
      />
      <div
        id="sections-grouplist-table-container"
        className="user-setup-table table-main-container h-[85%]"
        style={{ ...styles.tableContainer }}
      >
        <RMTable
          columns={getTableColumns}
          data={groupData}
          onCheckboxSelectionChange={handleSelectionChange}
          selectedIds={selectedRows}
          mainContainerStyle={styles.tableMainContainer}
        />
      </div>
    </>
  );
};

const styles: IStyle = {
  headerContainer: {
    marginTop: '1%',
    marginRight: '2.5%',
    marginBottom: 0,
  },
  headerTitle: {
    fontWeight: 'bold',
  },
  tableMainContainer: {
    overflow: 'auto',
  },
};

export default GroupList;
